/**
 * Main Layout Component
 * Combines Header and Sidebar with main content area
 * Responsive: Desktop sidebar / Mobile drawer
 */

import React, { useState } from 'react';
import { Box, Drawer, useMediaQuery, useTheme } from '@mui/material';
import { Sidebar } from './Sidebar';
import { MobileHeader } from './MobileHeader';
import { SignInModal } from '../auth/SignInModal';
import { useAuth } from '../../contexts/AuthContext';
import TrialBanner from '../subscription/TrialBanner';
import { SidebarConversationsProvider } from './SidebarConversationsProvider';
import { ProjectsProvider } from '../../features/projects/context/ProjectsProvider';
import { useTranslation } from 'react-i18next';
import { AppRealtimeProvider } from '../../hooks/useAppRealtimeEvents';
import { useQuotaRealtimeSync } from '../../hooks/useQuotaRealtimeSync';
import { NotificationsProvider } from '../../features/notifications/NotificationsProvider';
import { CriticalNotificationBanner } from '../../features/notifications/components/CriticalNotificationBanner';
import { AmbientBackground } from '../backgrounds/AmbientBackground';
import { useAppearance } from '../../contexts/AppearanceContext';

// ============================================
// Props Interface
// ============================================

interface LayoutProps {
  children: React.ReactNode;
  header?: React.ReactNode;
  onOpenSettings?: () => void;
}

// ============================================
// Layout Component
// ============================================

export const Layout: React.FC<LayoutProps> = ({ children, header, onOpenSettings }) => {
  const { t } = useTranslation();
  const { user, isAuthenticated, isAnonymous } = useAuth();
  const { backgroundTheme, hasPageBackground } = useAppearance();
  const isAmbientBackgroundOn = backgroundTheme !== 'none' && !hasPageBackground;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg')); // < 1024px
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);

  const handleToggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const handleMobileMenuOpen = () => {
    setIsMobileDrawerOpen(true);
  };

  const handleMobileMenuClose = () => {
    setIsMobileDrawerOpen(false);
  };

  if (!user) {
    // In production, show login page or redirect
    return (
      <Box className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-slate-900">
        <div>{t('app.loginRequired')}</div>
      </Box>
    );
  }

  const layoutContent = (
    <SidebarConversationsProvider>
      <ProjectsProvider>
        <Box className="flex min-h-screen bg-gray-50 dark:bg-slate-900">
          {/* Desktop: Fixed Full-Height Sidebar (hidden on mobile) */}
          {!isMobile && (
            <Sidebar
              user={user}
              isCollapsed={isSidebarCollapsed}
              onToggleCollapse={handleToggleSidebar}
              onOpenSettings={onOpenSettings}
              onSignIn={() => setIsSignInModalOpen(true)}
            />
          )}

          {/* Mobile: Drawer Navigation */}
          {isMobile && (
            <>
              <MobileHeader
                user={user}
                isAnonymous={isAnonymous}
                onMenuClick={handleMobileMenuOpen}
                onSignIn={() => setIsSignInModalOpen(true)}
              />
              <Drawer
                anchor="left"
                open={isMobileDrawerOpen}
                onClose={handleMobileMenuClose}
                ModalProps={{
                  keepMounted: true, // Better mobile performance
                }}
                PaperProps={{
                  className: 'w-64',
                }}
              >
                <Sidebar
                  user={user}
                  isCollapsed={false}
                  onToggleCollapse={() => {}} // No-op for drawer
                  isMobileDrawer={true}
                  onNavigate={handleMobileMenuClose}
                  onOpenSettings={onOpenSettings}
                  onSignIn={() => {
                    handleMobileMenuClose();
                    setIsSignInModalOpen(true);
                  }}
                />
              </Drawer>
            </>
          )}

          {isAnonymous && (
            <SignInModal
              isOpen={isSignInModalOpen}
              initialMode="signin"
              onClose={() => setIsSignInModalOpen(false)}
            />
          )}

          {/* Main Content Area - Positioned to the right of sidebar (desktop) or full width (mobile) */}
          <Box
            className={`relative flex min-h-screen min-w-0 flex-1 flex-col transition-all duration-300 ${
              isMobile ? 'pt-16' : isSidebarCollapsed ? 'ml-16' : 'ml-64'
            }`}
          >
            <AmbientBackground
              scope="layout"
              className={`pointer-events-none fixed bottom-0 right-0 z-0 ${
                isMobile ? 'left-0 top-16' : `top-0 ${isSidebarCollapsed ? 'left-16' : 'left-64'}`
              }`}
            />

            <Box
              className="relative z-[1] flex min-h-0 flex-1 flex-col"
              data-ambient-background={isAmbientBackgroundOn ? 'on' : 'off'}
            >
              {/* Header - Sticky at top of content area (desktop only, mobile has MobileHeader) */}
              {!isMobile && header}

              {/* Critical notifications */}
              {!isAnonymous && <CriticalNotificationBanner />}

              {/* Trial Banner - Shows when trial is expiring soon */}
              <Box className="p-4 empty:hidden">
                <TrialBanner />
              </Box>

              {/* Page Content */}
              <Box className="min-h-0 flex-1">{children}</Box>
            </Box>
          </Box>
        </Box>
      </ProjectsProvider>
    </SidebarConversationsProvider>
  );

  return (
    <AppRealtimeProvider enabled={isAuthenticated && !isAnonymous}>
      {isAuthenticated && !isAnonymous ? (
        <NotificationsProvider>
          <QuotaRealtimeSync />
          {layoutContent}
        </NotificationsProvider>
      ) : (
        layoutContent
      )}
    </AppRealtimeProvider>
  );
};

/** Renders nothing; lives under `AppRealtimeProvider` so it can subscribe to quota pushes. */
const QuotaRealtimeSync: React.FC = () => {
  useQuotaRealtimeSync();
  return null;
};
